import { useState, useMemo } from "react";
import { ClipboardPaste, X, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function uid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// Column presets — the header row a user pastes from their Excel usually
// looks like one of these. Order matters (matches what we read from the row).
const PRESETS = [
  {
    key: "l_w_h_wt",
    label: "L · W · H · Weight (4 cols)",
    fields: ["length", "width", "height", "weight"],
    example: "4000\t2960\t1890\t23000",
  },
  {
    key: "caseno_l_w_h_wt",
    label: "Case No · L · W · H · Weight (5 cols)",
    fields: ["case_no", "length", "width", "height", "weight"],
    example: "PC-1\t4000\t2960\t1890\t23000",
  },
  {
    key: "caseno_desc_l_w_h_wt",
    label: "Case No · Desc · L · W · H · Weight (6 cols)",
    fields: ["case_no", "description", "length", "width", "height", "weight"],
    example: "PC-1\tPress Base\t4000\t2960\t1890\t23000",
  },
  {
    key: "caseno_desc_qty_l_w_h_wt",
    label: "Case No · Desc · Qty · L · W · H · Weight (7 cols)",
    fields: ["case_no", "description", "quantity", "length", "width", "height", "weight"],
    example: "PC-1\tPress Base\t2\t4000\t2960\t1890\t23000",
  },
];

// Parse a raw pasted block into rows of fields (splits on tab, comma, or
// multiple spaces — whichever separates most columns).
function parseRows(text) {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  return lines.map((line) => {
    // Prefer tab (Excel default). Fall back to comma. Last resort: 2+ spaces.
    if (line.includes("\t")) return line.split("\t").map((s) => s.trim());
    if (line.includes(",")) return line.split(",").map((s) => s.trim());
    return line.split(/\s{2,}/).map((s) => s.trim());
  });
}

// Convert a numeric string to cm given the paste unit.
function toCm(v, unit) {
  const n = Number(String(v).replace(/[^\d.\-]/g, ""));
  if (!Number.isFinite(n)) return 0;
  return unit === "mm" ? n / 10 : n;
}

// Detect if the first row is a header (all cells non-numeric).
function looksLikeHeader(row) {
  const numericCells = row.filter((c) => {
    const n = Number(String(c).replace(/[^\d.\-]/g, ""));
    return c && Number.isFinite(n) && n !== 0;
  });
  // If more than half the cells are non-numeric it's very likely a header.
  return numericCells.length < row.length / 2;
}

export default function BulkPasteDialog({ show, onClose, onImport }) {
  const [preset, setPreset] = useState(PRESETS[2].key);
  const [unit, setUnit] = useState("mm");  // MM matches Excel packing-list style
  const [raw, setRaw] = useState("");

  const activePreset = useMemo(
    () => PRESETS.find((p) => p.key === preset) || PRESETS[0],
    [preset]
  );

  const parsedRows = useMemo(() => {
    if (!raw.trim()) return [];
    const rows = parseRows(raw);
    if (!rows.length) return [];
    return looksLikeHeader(rows[0]) ? rows.slice(1) : rows;
  }, [raw]);

  const previewCases = useMemo(() => {
    const out = [];
    const { fields } = activePreset;
    for (const row of parsedRows) {
      const rec = {
        id: uid(),
        case_no: "",
        description: "",
        quantity: 1,
        length_cm: 0,
        width_cm: 0,
        height_cm: 0,
        weight_kg: 0,
      };
      fields.forEach((f, idx) => {
        const val = row[idx] ?? "";
        if (f === "case_no") rec.case_no = String(val).trim();
        else if (f === "description") rec.description = String(val).trim();
        else if (f === "quantity") {
          const n = parseInt(String(val).replace(/[^\d]/g, ""), 10);
          rec.quantity = Number.isFinite(n) && n > 0 ? n : 1;
        } else if (f === "length") rec.length_cm = toCm(val, unit);
        else if (f === "width") rec.width_cm = toCm(val, unit);
        else if (f === "height") rec.height_cm = toCm(val, unit);
        else if (f === "weight") {
          // Weight rules: paste in kg by default. If MT ≤ 100 assume metric tons.
          const n = Number(String(val).replace(/[^\d.\-]/g, ""));
          rec.weight_kg = Number.isFinite(n) ? n : 0;
        }
      });
      // Auto-generate case_no if missing.
      if (!rec.case_no) rec.case_no = `C-${out.length + 1}`;
      out.push(rec);
    }
    return out;
  }, [parsedRows, activePreset, unit]);

  const validCount = previewCases.filter(
    (c) => c.length_cm > 0 && c.width_cm > 0 && c.height_cm > 0 && c.weight_kg > 0
  ).length;

  const handleImport = () => {
    if (!previewCases.length) {
      toast.error("Nothing to import — paste some rows first");
      return;
    }
    if (validCount === 0) {
      toast.error("No valid rows — check the column preset & unit");
      return;
    }
    onImport(previewCases);
    toast.success(`Imported ${previewCases.length} case${previewCases.length > 1 ? "s" : ""}`);
    setRaw("");
    onClose();
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/25 backdrop-blur flex items-center justify-center p-4"
      data-testid="bulk-paste-dialog"
      onClick={onClose}
    >
      <div
        className="bg-white border border-slate-200 w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl shadow-slate-900/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-[#FFFFFF]">
          <div className="flex items-center gap-2">
            <ClipboardPaste className="w-5 h-5 text-[#2563EB]" />
            <div>
              <div className="overline">Skip typing</div>
              <div className="heading text-lg font-semibold">Paste from Excel</div>
            </div>
          </div>
          <button
            data-testid="close-bulk-paste"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-xs text-[#64748B] leading-relaxed">
            Copy your dimensions block from Excel (Ctrl+C) and paste below. Tabs
            or commas both work. The first row is auto-detected as a header if
            it's non-numeric.
          </p>

          {/* Preset picker */}
          <div>
            <div className="overline mb-2">Column Order</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  data-testid={`bulk-preset-${p.key}`}
                  onClick={() => setPreset(p.key)}
                  className={
                    "text-left mono text-xs px-3 py-2 border transition-colors " +
                    (preset === p.key
                      ? "border-[#2563EB] bg-[#2563EB]/10 text-white"
                      : "border-slate-200 text-slate-600 hover:border-white/25 hover:text-white")
                  }
                >
                  <div className="font-semibold">{p.label}</div>
                  <div className="text-[10px] text-[#64748B] mt-0.5 truncate">
                    e.g. {p.example}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Unit picker */}
          <div className="flex items-center gap-3">
            <div className="overline">Dimensions Unit</div>
            <div className="inline-flex border border-slate-200">
              {["mm", "cm"].map((u) => (
                <button
                  key={u}
                  type="button"
                  data-testid={`bulk-unit-${u}`}
                  onClick={() => setUnit(u)}
                  className={
                    "px-3 py-1.5 mono text-[10px] uppercase tracking-widest transition-colors " +
                    (unit === u
                      ? "bg-[#2563EB] text-white font-semibold"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-100")
                  }
                >
                  {u}
                </button>
              ))}
            </div>
            <span className="mono text-[10px] text-slate-400">
              Weight is always in KG
            </span>
          </div>

          {/* Paste area */}
          <div>
            <div className="overline mb-2">Paste Rows</div>
            <textarea
              data-testid="bulk-paste-textarea"
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              placeholder={`Case No\tDescription\tL\tW\tH\tWeight\n${activePreset.example}\n${activePreset.example.replace(/PC-1/, "PC-2")}`}
              rows={8}
              className="w-full bg-[#FFFFFF] border border-slate-200 rounded-none focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] mono text-xs p-3 text-white/90"
              style={{ whiteSpace: "pre", fontFamily: "monospace" }}
            />
          </div>

          {/* Preview */}
          {previewCases.length > 0 && (
            <div className="border border-slate-200" data-testid="bulk-preview">
              <div className="px-3 py-2 border-b border-slate-200 bg-white/[0.02] flex items-center justify-between">
                <span className="mono text-[10px] uppercase tracking-widest text-slate-500">
                  Preview
                </span>
                <span className="mono text-[10px] text-[#2563EB] flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> {validCount} of {previewCases.length} valid
                </span>
              </div>
              <div className="max-h-52 overflow-y-auto">
                <table className="w-full text-[11px]">
                  <thead className="bg-[#FFFFFF] sticky top-0">
                    <tr className="border-b border-slate-200">
                      <th className="text-left px-2 py-1.5 overline">Case No</th>
                      <th className="text-left px-2 py-1.5 overline">Desc</th>
                      <th className="text-right px-2 py-1.5 overline">Qty</th>
                      <th className="text-right px-2 py-1.5 overline">L cm</th>
                      <th className="text-right px-2 py-1.5 overline">W cm</th>
                      <th className="text-right px-2 py-1.5 overline">H cm</th>
                      <th className="text-right px-2 py-1.5 overline">Wt kg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewCases.map((c, i) => {
                      const valid = c.length_cm > 0 && c.width_cm > 0 && c.height_cm > 0 && c.weight_kg > 0;
                      return (
                        <tr
                          key={c.id}
                          className={"border-b border-slate-100 " + (valid ? "" : "opacity-50")}
                        >
                          <td className="px-2 py-1.5 mono">{c.case_no}</td>
                          <td className="px-2 py-1.5 truncate max-w-[140px]">{c.description}</td>
                          <td className="px-2 py-1.5 mono text-right">{c.quantity}</td>
                          <td className="px-2 py-1.5 mono text-right">{c.length_cm}</td>
                          <td className="px-2 py-1.5 mono text-right">{c.width_cm}</td>
                          <td className="px-2 py-1.5 mono text-right">{c.height_cm}</td>
                          <td className="px-2 py-1.5 mono text-right">{c.weight_kg}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="p-5 border-t border-slate-200 flex justify-end gap-2 bg-[#FFFFFF] sticky bottom-0">
          <Button
            variant="ghost"
            data-testid="bulk-cancel-btn"
            onClick={onClose}
            className="rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </Button>
          <Button
            data-testid="bulk-import-btn"
            onClick={handleImport}
            disabled={validCount === 0}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-md font-semibold mono uppercase tracking-widest text-xs disabled:opacity-40"
          >
            Import {previewCases.length ? `(${previewCases.length})` : ""}
          </Button>
        </div>
      </div>
    </div>
  );
}
